import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { db, storage } from '@/core/firebase';
import { rankSwapExercises } from './exerciseSwap';
import type { Exercise, Media, WorkoutSession } from '../../../types';

async function hydrateExerciseMedia(media: Media[]) {
  return Promise.all(
    media.map(async (item) => {
      if (item.downloadUrl || item.externalUrl || !item.storagePath) return item;

      try {
        const downloadUrl = await getDownloadURL(ref(storage, item.storagePath));
        return { ...item, downloadUrl };
      } catch {
        return item;
      }
    })
  );
}

interface UseExerciseSwapArgs {
  session: WorkoutSession | null;
  onUpdateSession: (session: WorkoutSession) => Promise<void>;
}

export function useExerciseSwap({ session, onUpdateSession }: UseExerciseSwapArgs) {
  const [availableExercises, setAvailableExercises] = useState<Exercise[]>([]);
  const [availableExercisesLoaded, setAvailableExercisesLoaded] = useState(false);
  const [swapModalItemId, setSwapModalItemId] = useState<string | null>(null);
  const [swapOptions, setSwapOptions] = useState<Exercise[]>([]);
  const [swapLoading, setSwapLoading] = useState(false);
  const [swapSaving, setSwapSaving] = useState(false);
  const [swapError, setSwapError] = useState<string | null>(null);

  const swapItem = useMemo(
    () => session?.workoutSnapshot.items.find((item) => item.id === swapModalItemId) || null,
    [session, swapModalItemId]
  );

  // Load trainer exercises + shared library exercises
  useEffect(() => {
    if (!session?.trainerId) {
      setAvailableExercises([]);
      setAvailableExercisesLoaded(false);
      return;
    }

    setAvailableExercisesLoaded(false);

    const trainerQuery = query(
      collection(db, 'exercises'),
      where('trainerId', '==', session.trainerId)
    );
    const libraryQuery = query(
      collection(db, 'exercises'),
      where('trainerId', '==', 'library')
    );

    let trainerDocs: Exercise[] = [];
    let libraryDocs: Exercise[] = [];
    let trainerLoaded = false;
    let libraryLoaded = false;

    function mergeAndSet() {
      if (!trainerLoaded || !libraryLoaded) return;
      const seen = new Set<string>();
      const merged: Exercise[] = [];
      // Trainer exercises first so they win dedup
      for (const doc of [...trainerDocs, ...libraryDocs]) {
        if (!seen.has(doc.id)) {
          seen.add(doc.id);
          merged.push(doc);
        }
      }
      setAvailableExercises(merged);
      setAvailableExercisesLoaded(true);
    }

    const unsubTrainer = onSnapshot(
      trainerQuery,
      (snapshot) => {
        trainerDocs = snapshot.docs.map(
          (docSnap) => ({ id: docSnap.id, ...docSnap.data() } as Exercise)
        );
        trainerLoaded = true;
        mergeAndSet();
      },
      (err) => {
        console.error('Failed to load trainer swap exercises', err);
        trainerDocs = [];
        trainerLoaded = true;
        mergeAndSet();
      }
    );

    const unsubLibrary = onSnapshot(
      libraryQuery,
      (snapshot) => {
        libraryDocs = snapshot.docs.map(
          (docSnap) => ({ id: docSnap.id, ...docSnap.data() } as Exercise)
        );
        libraryLoaded = true;
        mergeAndSet();
      },
      (err) => {
        console.error('Failed to load library swap exercises', err);
        libraryDocs = [];
        libraryLoaded = true;
        mergeAndSet();
      }
    );

    return () => {
      unsubTrainer();
      unsubLibrary();
    };
  }, [session?.trainerId]);

  // Compute swap options when modal opens
  useEffect(() => {
    if (!swapModalItemId) return;

    if (!swapItem || !session?.trainerId) {
      setSwapOptions([]);
      setSwapLoading(false);
      setSwapError('Unable to load this exercise for swapping.');
      return;
    }

    if (!swapItem.targetMuscleGroups || swapItem.targetMuscleGroups.length === 0) {
      setSwapOptions([]);
      setSwapLoading(false);
      setSwapError('This exercise does not have target muscle groups configured yet.');
      return;
    }

    if (!availableExercisesLoaded) {
      setSwapLoading(true);
      return;
    }

    const rankedOptions = rankSwapExercises({
      currentExerciseId: swapItem.exerciseId,
      trainerId: session.trainerId,
      targetMuscleGroups: swapItem.targetMuscleGroups,
      exercises: availableExercises,
    });

    setSwapOptions(rankedOptions.map((entry) => entry.exercise));
    setSwapError(null);
    setSwapLoading(false);
  }, [availableExercises, availableExercisesLoaded, session, swapItem, swapModalItemId]);

  const openSwapModal = (itemId: string) => {
    setSwapModalItemId(itemId);
    setSwapOptions([]);
    setSwapError(null);
    setSwapLoading(true);
  };

  const closeSwapModal = () => {
    if (swapSaving) return;
    setSwapModalItemId(null);
    setSwapOptions([]);
    setSwapError(null);
    setSwapLoading(false);
  };

  const handleExerciseSwap = async (nextExercise: Exercise) => {
    if (!session || !swapItem) return;

    setSwapSaving(true);
    setSwapError(null);

    try {
      const resolvedMedia = await hydrateExerciseMedia(nextExercise.media || []);
      const originalExerciseId = swapItem.exerciseSwap?.originalExerciseId || swapItem.exerciseId;
      const exerciseSwap =
        nextExercise.id === originalExerciseId
          ? undefined
          : {
              originalExerciseId,
              swappedExerciseId: nextExercise.id,
            };

      const updatedItems = session.workoutSnapshot.items.map((item) => {
        if (item.id !== swapItem.id) return item;

        return {
          ...item,
          exerciseId: nextExercise.id,
          exerciseName: nextExercise.name,
          exerciseDescription: nextExercise.description,
          targetMuscleGroups: nextExercise.targetMuscleGroups,
          howTo: nextExercise.howTo,
          defaultCues: nextExercise.defaultCues,
          movementPattern: nextExercise.movementPattern,
          equipmentTags: nextExercise.equipmentTags,
          media: resolvedMedia,
          exerciseSwap,
        };
      });

      await onUpdateSession({
        ...session,
        workoutSnapshot: {
          ...session.workoutSnapshot,
          items: updatedItems,
        },
      });

      setSwapModalItemId(null);
      setSwapOptions([]);
    } catch (err) {
      console.error('Failed to swap exercise', err);
      setSwapError('Unable to swap this exercise right now.');
    } finally {
      setSwapSaving(false);
      setSwapLoading(false);
    }
  };

  return {
    swapItem,
    swapOptions,
    swapLoading,
    swapSaving,
    swapError,
    openSwapModal,
    closeSwapModal,
    handleExerciseSwap,
  };
}
